// ============================================================================
// use-page-context — maps the current React Router route to structured
// PageContext metadata for the AI chat system's context awareness engine.
// ============================================================================

import { useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import type { ContextPayload } from "@/lib/api/chat";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Page context derived from the current route. This is the subset of
 * ContextPayload that can be determined without auth information — the
 * `userRole` field is added downstream by the action filter / integration.
 */
export interface PageContext {
  currentPage: string;
  pageType: string;
  objectId?: string;
  objectType?: string;
  availableActions: string[];
  breadcrumbs: string[];
}

// ---------------------------------------------------------------------------
// Route context mapping
// ---------------------------------------------------------------------------

interface RouteContextEntry {
  /** Regex pattern matching the pathname */
  pattern: RegExp;
  pageType: string;
  objectType?: string;
  /** Whether this route uses a dynamic :param for the object ID */
  hasObjectId: boolean;
  /** The param name to extract from useParams() (e.g. "epicId") */
  paramName?: string;
  availableActions: string[];
}

/**
 * Ordered list of route context entries. Order matters — the first match wins.
 * More specific routes (with params) must come before their parent list routes.
 */
const ROUTE_CONTEXT_MAP: RouteContextEntry[] = [
  // Dashboard
  {
    pattern: /^\/dashboard\/?$/,
    pageType: "dashboard",
    hasObjectId: false,
    availableActions: ["view-metrics", "view-activity"],
  },
  // Inbox
  {
    pattern: /^\/inbox\/?$/,
    pageType: "inbox",
    hasObjectId: false,
    availableActions: ["view-activity"],
  },
  // What's New
  {
    pattern: /^\/whats-new\/?$/,
    pageType: "whats-new",
    hasObjectId: false,
    availableActions: ["view-activity"],
  },
  // Epic detail (must be before epic-list)
  {
    pattern: /^\/epics\/[^/]+\/?$/,
    pageType: "epic-detail",
    objectType: "epic",
    hasObjectId: true,
    paramName: "epicId",
    availableActions: [
      "execute-epic",
      "edit-epic",
      "add-feature",
      "view-description",
    ],
  },
  // Epic list
  {
    pattern: /^\/epics\/?$/,
    pageType: "epic-list",
    hasObjectId: false,
    availableActions: ["create-epic", "filter", "search"],
  },
  // Epic request detail (must be before epic-request-list)
  {
    pattern: /^\/epic-requests\/[^/]+\/?$/,
    pageType: "epic-request-detail",
    objectType: "epic-request",
    hasObjectId: true,
    paramName: "requestId",
    availableActions: ["view-request", "react", "comment"],
  },
  // Epic request list
  {
    pattern: /^\/epic-requests\/?$/,
    pageType: "epic-request-list",
    hasObjectId: false,
    availableActions: ["create-request", "filter", "search"],
  },
  // Feature detail
  {
    pattern: /^\/features\/[^/]+\/?$/,
    pageType: "feature-detail",
    objectType: "feature",
    hasObjectId: true,
    paramName: "featureId",
    availableActions: ["edit-feature", "add-task", "change-status"],
  },
  // Task detail
  {
    pattern: /^\/tasks\/[^/]+\/?$/,
    pageType: "task-detail",
    objectType: "task",
    hasObjectId: true,
    paramName: "taskId",
    availableActions: ["edit-task", "change-status"],
  },
  // Team detail (must be before team-list)
  {
    pattern: /^\/teams\/[^/]+\/?$/,
    pageType: "team-detail",
    objectType: "team",
    hasObjectId: true,
    paramName: "teamId",
    availableActions: ["manage-members", "manage-settings"],
  },
  // Team list
  {
    pattern: /^\/teams\/?$/,
    pageType: "team-list",
    hasObjectId: false,
    availableActions: ["view-team", "manage-members"],
  },
  // Settings
  {
    pattern: /^\/settings\/?$/,
    pageType: "settings",
    hasObjectId: false,
    availableActions: ["manage-settings"],
  },
  // Admin users
  {
    pattern: /^\/admin\/users\/?$/,
    pageType: "admin-users",
    hasObjectId: false,
    availableActions: ["manage-users"],
  },
];

// ---------------------------------------------------------------------------
// Breadcrumb helper
// ---------------------------------------------------------------------------

/**
 * Build human-readable breadcrumbs from the pathname.
 * Strips leading slashes, decodes URI components, and capitalises segments.
 */
function buildBreadcrumbs(pathname: string): string[] {
  return pathname
    .split("/")
    .filter(Boolean)
    .map((segment) => decodeURIComponent(segment).replace(/\b\w/g, (c) => c.toUpperCase()));
}

// ---------------------------------------------------------------------------
// Default context for unrecognised routes
// ---------------------------------------------------------------------------

function defaultContext(pathname: string): PageContext {
  return {
    currentPage: pathname,
    pageType: "unknown",
    availableActions: [],
    breadcrumbs: buildBreadcrumbs(pathname),
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Custom hook that maps the current React Router location to a structured
 * {@link PageContext} object. The result is memoised on `pathname` and route
 * params so downstream consumers only re-render on actual navigation.
 *
 * **Note:** The returned context does NOT include `userRole`. That field is
 * added by the action-filter layer before being sent to the AI adapter.
 */
export function usePageContext(): PageContext {
  const { pathname } = useLocation();
  const params = useParams();

  return useMemo<PageContext>(() => {
    for (const entry of ROUTE_CONTEXT_MAP) {
      if (entry.pattern.test(pathname)) {
        const context: PageContext = {
          currentPage: pathname,
          pageType: entry.pageType,
          availableActions: [...entry.availableActions],
          breadcrumbs: buildBreadcrumbs(pathname),
        };

        if (entry.objectType) {
          context.objectType = entry.objectType;
        }

        if (entry.hasObjectId && entry.paramName) {
          const id = params[entry.paramName];
          if (id) {
            context.objectId = id;
          }
        }

        return context;
      }
    }

    return defaultContext(pathname);
  }, [pathname, params]);
}

/**
 * Convert a {@link PageContext} to a full {@link ContextPayload} by adding
 * the user's role and (optionally) preferred CLI host. Utility used by the
 * ChatProvider integration layer.
 */
export function toContextPayload(
  context: PageContext,
  userRole: string,
  preferredHost?: string,
): ContextPayload {
  return {
    ...context,
    userRole,
    ...(preferredHost ? { preferredHost } : {}),
  };
}
